import { memo, type ComponentType, useState } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Box,
  Text,
  Flex,
  Icon,
} from '@chakra-ui/react';
import {
  //   MdLightbulb,
  MdHelp,
  MdDescription,
} from 'react-icons/md';

import LogoutModal from 'components/LogoutModal';
import DimensionsIcon from 'components/Icons/DimensionsIcon';
import DimensionsIconActive from 'components/Icons/DimensionsIconActive';
import ImportDataIcon from 'components/Icons/ImportDataIcon';
import ImportDataIconActive from 'components/Icons/ImportDataIconActive';
import ContactSupportIcon from 'components/Icons/ContactSupportIcon';
import DashboardIcon from 'components/Icons/DashboardIcon';
import DashboardIconActive from 'components/Icons/DashboardIconActive';
import { accent, neutral, primaryAction, radius } from 'styles/revampTokens';

import { TabType } from '../types';

import BlocksIconActive from './icons/BlocksIconActive';
import BlocksIcon from './icons/BlocksIcon';
import UserMenuPanel from './UserMenuPanel';

interface MobileNavigationMenuProperties {
  isOpen: boolean;
  onClose: () => void;
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}

interface NavigationItem {
  tab: TabType;
  label: string;
  icon: ComponentType;
  activeIcon: ComponentType;
}

interface NavigationSectionProperties {
  activeTab: TabType;
  onMenuItemClick: (tab: TabType) => void;
}

const navigationItems: NavigationItem[] = [
  {
    tab: 'dashboard',
    label: 'Dashboard',
    icon: DashboardIcon,
    activeIcon: DashboardIconActive,
  },
  {
    tab: 'blocks',
    label: 'Blocks',
    icon: BlocksIcon,
    activeIcon: BlocksIconActive,
  },
  {
    tab: 'dimensions',
    label: 'Dimensions',
    icon: DimensionsIcon,
    activeIcon: DimensionsIconActive,
  },
  {
    tab: 'import',
    label: 'Data',
    icon: ImportDataIcon,
    activeIcon: ImportDataIconActive,
  },
];

const NavigationSection = ({ activeTab, onMenuItemClick }: NavigationSectionProperties) => (
  <Box p={6}>
    <Text fontSize='sm' fontWeight='bold' color='black' mb={4} letterSpacing='wide'>
      Navigation
    </Text>

    <Flex
      w='100%'
      bg='#E8E9EB'
      borderRadius={radius.overlay}
      border={`1px solid ${neutral.border}`}
      overflow='hidden'
      gap='1px'
      p='1px'
      flexDirection='column'
    >
      {navigationItems.map((item) => {
        const isActive = activeTab === item.tab;
        const TabIcon = isActive ? item.activeIcon : item.icon;

        return (
          <Flex
            key={item.tab}
            w='100%'
            h='40px'
            alignItems='center'
            gap={2}
            bg={isActive ? accent.solid : 'white'}
            color={isActive ? 'white' : primaryAction.bg}
            borderRadius={isActive ? '12px' : '0'}
            px={2}
            py={2}
            fontSize='14px'
            fontWeight='500'
            cursor='pointer'
            _hover={{ bg: isActive ? accent.solid : 'white' }}
            onClick={() => onMenuItemClick(item.tab)}
          >
            <Box boxSize={4} display='flex' alignItems='center' justifyContent='center'>
              <TabIcon />
            </Box>
            <Text flex='1 1 0'>{item.label}</Text>
          </Flex>
        );
      })}
    </Flex>
  </Box>
);

const MobileNavigationMenu = memo(
  ({ isOpen, onClose, activeTab, onTabChange }: MobileNavigationMenuProperties) => {
    const [logoutModal, setLogoutModal] = useState(false);

    const handleMenuItemClick = (tab: TabType) => {
      onTabChange(tab);
      onClose();
    };

    const handleHelpClick = () => {
      window.open('https://help.blox.so/', '_blank');
      onClose();
    };

    const handleContactClick = () => {
      window.open('https://www.blox.so/contact', '_blank');
      onClose();
    };

    return (
      <>
        <Drawer isOpen={isOpen} placement='right' onClose={onClose} size='sm'>
          <DrawerOverlay />
          <DrawerContent bg='white'>
            <DrawerBody p={0}>
              <Box h='100%' display='flex' flexDirection='column'>
                <Box px={4} pt={2} pb={2}>
                  <Box
                    w='100%'
                    color='black'
                    borderWidth='1px'
                    borderRadius={radius.overlay}
                    borderColor='gray.200'
                    bg='white'
                    boxShadow='0px 4px 12px rgba(0, 0, 0, 0.08)'
                    overflow='hidden'
                  >
                    <UserMenuPanel
                      onRequestClose={onClose}
                      onLogoutRequest={() => setLogoutModal(true)}
                    />
                  </Box>
                </Box>

                <NavigationSection activeTab={activeTab} onMenuItemClick={handleMenuItemClick} />

                {/* More Section */}
                <Box p={6} pt={0}>
                  <Text fontSize='16px' fontWeight='500' color={primaryAction.bg} mb={2}>
                    More
                  </Text>

                  <Flex
                    alignSelf='stretch'
                    bg='#E8E9EB'
                    borderRadius={radius.overlay}
                    border={`1px solid ${neutral.border}`}
                    overflow='hidden'
                    flexDirection='column'
                    gap='1px'
                  >
                    <Flex
                      w='100%'
                      bg='white'
                      alignItems='center'
                      gap='8px'
                      px={2}
                      py={3}
                      cursor='pointer'
                      _hover={{ bg: 'white' }}
                      onClick={handleHelpClick}
                    >
                      <Icon as={MdHelp} boxSize={4} color={primaryAction.bg} />
                      <Text color={primaryAction.bg} fontSize='14px' fontWeight='500'>
                        Help Center
                      </Text>
                    </Flex>

                    <Flex
                      w='100%'
                      bg='white'
                      alignItems='center'
                      gap='8px'
                      px={2}
                      py={3}
                      cursor='pointer'
                      _hover={{ bg: 'white' }}
                      onClick={onClose}
                    >
                      <Icon as={MdDescription} boxSize={4} color={primaryAction.bg} />
                      <Text color={primaryAction.bg} fontSize='14px' fontWeight='500'>
                        What's New
                      </Text>
                    </Flex>

                    <Flex
                      w='100%'
                      bg='white'
                      alignItems='center'
                      gap='8px'
                      px={2}
                      py={3}
                      cursor='pointer'
                      _hover={{ bg: 'white' }}
                      onClick={handleContactClick}
                    >
                      <Box boxSize={4} display='flex' alignItems='center' justifyContent='center'>
                        <ContactSupportIcon color={primaryAction.bg} />
                      </Box>
                      <Text color={primaryAction.bg} fontSize='14px' fontWeight='500'>
                        Contact Support
                      </Text>
                    </Flex>
                  </Flex>
                </Box>
              </Box>
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        {logoutModal && (
          <LogoutModal
            isOpen={logoutModal}
            onClose={() => setLogoutModal(false)}
            onOpenAuthModal={() => {}}
          />
        )}
      </>
    );
  },
);

MobileNavigationMenu.displayName = 'MobileNavigationMenu';

export default MobileNavigationMenu;
